import React from 'react';
import type { SxProps, Theme } from '@mui/material';
import { useBrandRegistry } from '../../hooks/useBrandRegistry';
import { CreatableSelect, CreatableSelectOption } from './CreatableSelect';

export interface BrandSelectProps {
  /** Brand name. */
  value: string;
  onChange: (value: string) => void;
  label?: string;
  helperText?: React.ReactNode;
  error?: boolean;
  disabled?: boolean;
  /** Offer "Use <typed>" for a brand that is not in the registry. Default true. */
  allowCreate?: boolean;
  sx?: SxProps<Theme>;
  'aria-label'?: string;
}

/** Brand picker over the brand registry; the brand's name is the value. */
export const BrandSelect: React.FC<BrandSelectProps> = ({
  value,
  onChange,
  label = 'Brand',
  helperText,
  error,
  disabled,
  allowCreate = true,
  sx,
  'aria-label': ariaLabel,
}) => {
  const { data, isLoading } = useBrandRegistry();

  const options = React.useMemo<CreatableSelectOption[]>(
    () =>
      (data || [])
        .filter((brand) => brand.name)
        .map((brand) => ({
          value: brand.name,
          hint: [brand.slug, brand.status].filter(Boolean).join(' · ') || undefined,
        }))
        .sort((a, b) => a.value.localeCompare(b.value)),
    [data],
  );

  const known = options.some((o) => o.value.toLowerCase() === value.trim().toLowerCase());
  const notListed = !allowCreate && !isLoading && value.trim() !== '' && !known;

  const handleChange = (next: string) => {
    onChange(next);
  };

  return (
    <CreatableSelect
      label={label}
      value={value}
      options={options}
      onChange={handleChange}
      placeholder={isLoading ? 'Loading brands...' : undefined}
      helperText={notListed ? 'Pick a brand from the list' : helperText}
      error={error || notListed}
      disabled={disabled}
      allowCreate={allowCreate}
      loading={isLoading}
      sx={sx}
      aria-label={ariaLabel}
    />
  );
};
